import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { displayDate, todayISO } from "@/lib/utils";
import { useNavigate, useSearchParams } from "react-router";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import Badge from "@/components/ui/badge/Badge";
import { Modal } from "@/components/ui/modal";
import ConfirmModal from "@/components/ui/modal/ConfirmModal";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import { payableService } from "@/services/payableService";
import { supplierService } from "@/services/masterDataService";
import { handleApiError } from "@/services/api";
import DateRangeFilter, {
  getDefaultDateRange,
} from "@/components/common/DateRangeFilter";
import AttachmentUpload from "@/components/form/AttachmentUpload";
import AttachmentViewer from "@/components/common/AttachmentViewer";
import type { Payable, Payment, Supplier, SupplierBalance, Attachment } from "@/types/models";
import { useIsOwner } from "@/hooks/useRole";
import { useSortable } from "@/hooks/useSortable";
import SortableHeader from "@/components/ui/table/SortableHeader";
import RecordCashModal from "@/components/finance/RecordCashModal";
import Pagination from "@/components/ui/pagination/Pagination";

const selectClass =
  "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

type Tab = "bills" | "balances";

const statusColor: Record<string, "success" | "warning" | "error"> = {
  paid: "success",
  partial: "warning",
  unpaid: "error",
};

const methodLabels: Record<string, string> = {
  cash: "Cash",
  bank_transfer: "Bank Transfer",
  cheque: "Cheque",
  upi: "UPI",
};

// Mirrors backend service.settledEpsilon (half a paisa). Used in the
// allocation preview to keep "paid in full" / "partial" labels in sync
// with the server's status recomputation logic.
const SETTLED_EPS = 0.005;

export default function PayablesPage() {
  const navigate = useNavigate();
  const isOwner = useIsOwner();

  const [tab, setTab] = useState<Tab>("bills");
  const { sortConfig, requestSort } = useSortable("date", "desc");

  // Bills state
  const [entries, setEntries] = useState<Payable[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const limit = 100;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Suppliers
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [showRecordPayment, setShowRecordPayment] = useState(false);

  // Filters — an alert can deep-link with ?status=unpaid; when it does, start from
  // an all-time range so the (older) overdue bills it points at are actually visible
  // instead of being hidden by the default recent-days window.
  const [searchParams] = useSearchParams();
  const urlStatus = searchParams.get("status") || "";
  const defaults = getDefaultDateRange();
  const [dateFrom, setDateFrom] = useState(urlStatus ? "" : defaults.from);
  const [dateTo, setDateTo] = useState(urlStatus ? "" : defaults.to);
  const [filterSupplier, setFilterSupplier] = useState("");
  const [filterStatus, setFilterStatus] = useState(urlStatus);

  // Balances
  const [balances, setBalances] = useState<SupplierBalance[]>([]);
  const [balancesLoading, setBalancesLoading] = useState(false);

  // Create/edit bill modal
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formSupplierId, setFormSupplierId] = useState("");
  const [formDate, setFormDate] = useState(todayISO());
  const [formDueDate, setFormDueDate] = useState("");
  const [formBillNumber, setFormBillNumber] = useState("");
  const [formAmount, setFormAmount] = useState("");
  const [formNotes, setFormNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [formAttachments, setFormAttachments] = useState<Attachment[]>([]);
  const [formAttachmentIds, setFormAttachmentIds] = useState<number[]>([]);

  // Edit-payment modal (opened from Payment History). The server deletes the
  // payment and re-records it via PUT /payables/payments/{id}, then re-settles
  // the supplier oldest-first. New payments are recorded with Record Payment
  // (cash) or a bank statement Tag — there is no per-bill pay here.
  const [showPayment, setShowPayment] = useState(false);
  const [payTarget, setPayTarget] = useState<Payable | null>(null);
  const [editingPaymentId, setEditingPaymentId] = useState<number | null>(null);
  // Captured at open-time so the preview rebaseline (subtract the old
  // amount before previewing the new allocation) doesn't depend on the
  // payments[] state being fresh — the history modal may have been closed
  // and never re-fetched.
  const [editingPaymentAmount, setEditingPaymentAmount] = useState<number>(0);
  // All other unpaid/partial bills for the same supplier — used to compute
  // the FIFO allocation preview when payment amount overflows.
  const [supplierOpenBills, setSupplierOpenBills] = useState<Payable[]>([]);
  const [payDate, setPayDate] = useState(todayISO());
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("cash");
  const [payReference, setPayReference] = useState("");
  const [payNotes, setPayNotes] = useState("");

  // Payment history modal
  const [showHistory, setShowHistory] = useState(false);
  const [historyPayable, setHistoryPayable] = useState<Payable | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchBills = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params: Record<string, string | number> = { limit, offset };
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      if (filterSupplier) params.supplier_id = parseInt(filterSupplier);
      if (filterStatus) params.status = filterStatus;
      params.sort_by = sortConfig.key;
      params.sort_order = sortConfig.direction;
      const res = await payableService.list(params);
      setEntries(res.data ?? []);
      setTotal(res.meta?.total ?? 0);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, filterSupplier, filterStatus, offset, sortConfig]);

  const fetchBalances = useCallback(async () => {
    setBalancesLoading(true);
    setError("");
    try {
      const data = await payableService.getBalances();
      setBalances(data ?? []);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setBalancesLoading(false);
    }
  }, []);

  useEffect(() => {
    supplierService.list().then(setSuppliers).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === "bills") fetchBills();
    else fetchBalances();
  }, [tab, fetchBills, fetchBalances]);

  const handleDateApply = (from: string, to: string) => {
    setDateFrom(from);
    setDateTo(to);
    setOffset(0);
  };

  const handlePrev = () => {
    setOffset((prev) => Math.max(0, prev - limit));
  };

  const handleNext = () => {
    setOffset((prev) => prev + limit);
  };

  const resetBillForm = () => {
    setEditingId(null);
    setFormSupplierId("");
    setFormDate(todayISO());
    setFormDueDate("");
    setFormBillNumber("");
    setFormAmount("");
    setFormNotes("");
    setFormAttachments([]);
    setFormAttachmentIds([]);
  };

  const openCreateModal = () => {
    resetBillForm();
    setError("");
    setShowCreate(true);
  };

  const openEditModal = (p: Payable) => {
    setEditingId(p.id);
    setFormSupplierId(String(p.supplier_id));
    setFormDate(p.date?.toString().slice(0, 10) ?? todayISO());
    setFormDueDate(p.due_date?.toString().slice(0, 10) ?? "");
    setFormBillNumber(p.bill_number || "");
    setFormAmount(String(p.total_amount));
    setFormNotes(p.notes || "");
    setFormAttachments([]);
    setFormAttachmentIds([]);
    setError("");
    setShowCreate(true);
  };

  const handleSaveBill = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        supplier_id: parseInt(formSupplierId),
        date: formDate,
        due_date: formDueDate || undefined,
        bill_number: formBillNumber || undefined,
        total_amount: parseFloat(formAmount),
        notes: formNotes || undefined,
      };
      if (editingId) {
        await payableService.update(editingId, payload);
      } else {
        await payableService.create({
          ...payload,
          attachment_ids: formAttachmentIds.length > 0 ? formAttachmentIds : undefined,
        });
      }
      setShowCreate(false);
      resetBillForm();
      fetchBills();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  // Delete confirmations use the styled ConfirmModal (not window.confirm) so
  // they match the rest of the app. State holds the id pending confirmation.
  const [confirmBillDelete, setConfirmBillDelete] = useState<number | null>(null);
  const [confirmPaymentDelete, setConfirmPaymentDelete] = useState<number | null>(null);

  const handleDeleteBill = async () => {
    if (confirmBillDelete === null) return;
    try {
      await payableService.delete(confirmBillDelete);
      setConfirmBillDelete(null);
      fetchBills();
    } catch (err) {
      setConfirmBillDelete(null);
      setError(handleApiError(err));
    }
  };

  // Aborts an in-flight supplier-bills fetch when the modal is closed or
  // reopened for a different supplier, so stale results don't overwrite
  // state. Also aborted on component unmount via the cleanup useEffect.
  const openBillsFetchRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      openBillsFetchRef.current?.abort();
    };
  }, []);

  // openPayModal opens the edit modal for an existing payment; the form
  // pre-fills and the submit goes through PUT /payables/payments/{id}.
  const openPayModal = (payable: Payable, editPayment: Payment) => {
    setError(""); // don't carry a stale error from another modal into this one
    openBillsFetchRef.current?.abort();
    const controller = new AbortController();
    openBillsFetchRef.current = controller;

    setPayTarget(payable);
    setEditingPaymentId(editPayment.id);
    setEditingPaymentAmount(editPayment.amount);
    setPayAmount(editPayment.amount.toString());
    setPayDate(editPayment.date?.toString().slice(0, 10) ?? todayISO());
    setPayMethod(editPayment.method);
    setPayReference(editPayment.reference || "");
    setPayNotes(editPayment.notes || "");
    setSupplierOpenBills([]);
    setShowPayment(true);

    // Pass the abort signal into axios so the HTTP request itself cancels
    // when the modal closes/reopens — not just the state update.
    // Two server-side filtered fetches (unpaid + partial) instead of a
    // 1000-row blanket pull. Avoids streaming paid bills back just to
    // discard them in JS, and removes the silent-truncation cliff for
    // suppliers with long histories.
    Promise.all([
      payableService.list({ supplier_id: payable.supplier_id, status: "unpaid", limit: 500 }, controller.signal),
      payableService.list({ supplier_id: payable.supplier_id, status: "partial", limit: 500 }, controller.signal),
    ])
      .then(([unpaidRes, partialRes]) => {
        if (controller.signal.aborted) return;
        const open = [...(unpaidRes.data ?? []), ...(partialRes.data ?? [])];
        setSupplierOpenBills(open);
      })
      .catch(() => {
        // preview is best-effort; submission still works without it
      });
  };

  // Plan how a payment amount will be split: clicked bill first, then FIFO
  // across other unpaid/partial bills for the same supplier, then the
  // remainder as a supplier credit. Mirrors the backend allocation.
  const allocationPreview = useMemo<{
    items: Array<{ payable: Payable; amount: number }>;
    advance: number;
  }>(() => {
    const empty = { items: [] as Array<{ payable: Payable; amount: number }>, advance: 0 };
    const amount = parseFloat(payAmount);
    if (!payTarget || isNaN(amount) || amount <= 0) return empty;

    // Prefer the freshly-fetched copy of the clicked bill over the row
    // passed into openPayModal (which can be stale if the operator paid
    // in another tab/session before opening this modal).
    let clicked =
      supplierOpenBills.find((b) => b.id === payTarget.id) ?? payTarget;

    // The server will first delete the old payment (zeroing its
    // contribution to paid_amount) and then re-record. Simulate that
    // baseline so the preview matches what will happen — otherwise editing
    // a payment that fully paid its bill shows a blank preview because the
    // bill is currently "paid" and got filtered out of supplierOpenBills.
    // editingPaymentAmount is captured at open-time so this works even if
    // the history modal was closed before opening this one.
    if (editingPaymentId && editingPaymentAmount > 0) {
      clicked = {
        ...clicked,
        paid_amount: Math.max(0, clicked.paid_amount - editingPaymentAmount),
      };
    }

    const items: Array<{ payable: Payable; amount: number }> = [];
    let remaining = amount;

    const clickedOutstanding = clicked.total_amount - clicked.paid_amount;
    const primaryAmount = Math.min(remaining, clickedOutstanding);
    if (primaryAmount > SETTLED_EPS) {
      items.push({ payable: clicked, amount: primaryAmount });
      remaining -= primaryAmount;
    }

    if (remaining > SETTLED_EPS) {
      const fifo = supplierOpenBills
        .filter((b) => b.id !== clicked.id && b.status !== "paid")
        .sort((a, b) => a.date.localeCompare(b.date) || a.id - b.id);
      for (const o of fifo) {
        if (remaining <= SETTLED_EPS) break;
        const outstanding = o.total_amount - o.paid_amount;
        if (outstanding <= SETTLED_EPS) continue;
        const take = Math.min(remaining, outstanding);
        items.push({ payable: o, amount: take });
        remaining -= take;
      }
    }

    return { items, advance: remaining > SETTLED_EPS ? remaining : 0 };
  }, [payAmount, payTarget, supplierOpenBills, editingPaymentId, editingPaymentAmount]);

  const handleRecordPayment = async () => {
    if (!payTarget || !editingPaymentId) return;
    setSaving(true);
    setError("");
    try {
      await payableService.updatePayment(editingPaymentId, {
        payable_id: payTarget.id,
        date: payDate,
        amount: parseFloat(payAmount) || 0,
        method: payMethod,
        reference: payReference || undefined,
        notes: payNotes || undefined,
      });
      setShowPayment(false);
      setPayTarget(null);
      setEditingPaymentId(null);
      setEditingPaymentAmount(0);
      fetchBills();
      // Refresh the payments list whenever a history-rooted modal was the
      // entry point. showHistory is already false at this point because
      // openPayModal closes the history modal before opening the pay
      // modal — so guard on historyPayable alone, not the visibility flag.
      if (historyPayable) {
        const refreshed = await payableService.listPayments(historyPayable.id);
        setPayments(refreshed ?? []);
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePayment = async () => {
    if (confirmPaymentDelete === null) return;
    const paymentID = confirmPaymentDelete;
    // Keep the modal open (showing the busy label) until the delete resolves,
    // so it can't be double-submitted; clear it once done.
    setError("");
    setSaving(true);
    try {
      await payableService.deletePayment(paymentID);
      setConfirmPaymentDelete(null);
      fetchBills();
      if (historyPayable) {
        const refreshed = await payableService.listPayments(historyPayable.id);
        setPayments(refreshed ?? []);
      }
    } catch (err) {
      setConfirmPaymentDelete(null);
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const openPaymentHistory = async (payable: Payable) => {
    setError(""); // don't carry a stale error from another modal into this one
    setHistoryPayable(payable);
    setShowHistory(true);
    setHistoryLoading(true);
    try {
      const data = await payableService.listPayments(payable.id);
      setPayments(data ?? []);
    } catch {
      // silently fail
    } finally {
      setHistoryLoading(false);
    }
  };

  const totalOutstanding = entries
    .filter((e) => e.status !== "paid")
    .reduce((sum, e) => sum + (e.total_amount - e.paid_amount), 0);

  return (
    <>
      <PageMeta title="Accounts Payable | Poultry Book" description="Track supplier bills and payments" />
      <PageBreadcrumb pageTitle="Accounts Payable" />

      {/* Tabs */}
      <div className="mb-4 flex gap-1 rounded-lg border border-gray-200 p-0.5 dark:border-gray-700 w-fit">
        {([["bills", "Bills"], ["balances", "Supplier Balances"]] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
              tab === key
                ? "bg-brand-500 text-white"
                : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {error && !showCreate && !showPayment && !showHistory && (
        <div className="mb-4 rounded-lg bg-error-50 px-4 py-3 text-sm text-error-600 dark:bg-error-500/10 dark:text-error-400">
          {error}
        </div>
      )}

      {/* ---- BILLS TAB ---- */}
      {tab === "bills" && (
        <>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <DateRangeFilter onApply={handleDateApply} />
              <select value={filterSupplier} onChange={(e) => setFilterSupplier(e.target.value)} className={selectClass + " !w-auto !h-8 !px-2 !py-0 !text-xs"}>
                <option value="">All Suppliers</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className={selectClass + " !w-auto !h-8 !px-2 !py-0 !text-xs"}>
                <option value="">All Status</option>
                <option value="unpaid">Unpaid</option>
                <option value="partial">Partial</option>
                <option value="paid">Paid</option>
              </select>
            </div>
            <div className="flex items-center gap-4">
              {totalOutstanding > 0 && (
                <span className="text-sm font-medium text-error-600 dark:text-error-400">
                  Outstanding: {"\u20B9"}{totalOutstanding.toLocaleString("en-IN")}
                </span>
              )}
              {isOwner && (
                <Button size="sm" variant="outline" onClick={() => setShowRecordPayment(true)}>
                  Record Payment
                </Button>
              )}
              {isOwner && (
                <Button size="sm" onClick={openCreateModal}>
                  + New Bill
                </Button>
              )}
            </div>
          </div>

          <div className="overflow-hidden card">
            {loading ? (
              <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">Loading...</div>
            ) : entries.length === 0 && !error ? (
              <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">No bills in the selected period</div>
            ) : entries.length === 0 ? null : (
              <div className="max-w-full overflow-x-auto">
                <Table padded>
                  <TableHeader>
                    <TableRow>
                      <SortableHeader label="Date" sortKey="date" sortConfig={sortConfig} onSort={requestSort} />
                      <SortableHeader label="Supplier" sortKey="supplier" sortConfig={sortConfig} onSort={requestSort} />
                      <SortableHeader label="Bill #" sortKey="bill_number" sortConfig={sortConfig} onSort={requestSort} />
                      <SortableHeader label="Amount" sortKey="amount" sortConfig={sortConfig} onSort={requestSort} />
                      <SortableHeader label="Paid" sortKey="paid" sortConfig={sortConfig} onSort={requestSort} />
                      <SortableHeader label="Outstanding" sortKey="outstanding" sortConfig={sortConfig} onSort={requestSort} />
                      <SortableHeader label="Due Date" sortKey="due_date" sortConfig={sortConfig} onSort={requestSort} />
                      <SortableHeader label="Status" sortKey="status" sortConfig={sortConfig} onSort={requestSort} />
                      <TableCell isHeader>Actions</TableCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.map((p) => {
                      const outstanding = p.total_amount - p.paid_amount;
                      const isOverdue = p.due_date && p.status !== "paid" && new Date(p.due_date) < new Date();
                      return (
                        <TableRow key={p.id}>
                          <TableCell>{displayDate(p.date)}</TableCell>
                          <TableCell>{p.supplier_name}</TableCell>
                          <TableCell>{p.bill_number || "-"}</TableCell>
                          <TableCell>{"\u20B9"}{p.total_amount.toLocaleString("en-IN")}</TableCell>
                          <TableCell>{"\u20B9"}{p.paid_amount.toLocaleString("en-IN")}</TableCell>
                          <TableCell className={outstanding > 0 ? "font-medium text-error-600 dark:text-error-400" : ""}>
                            {"\u20B9"}{outstanding.toLocaleString("en-IN")}
                          </TableCell>
                          <TableCell>
                            {p.due_date ? (
                              <span className={isOverdue ? "text-error-600 dark:text-error-400 font-medium" : ""}>
                                {displayDate(p.due_date)}
                                {isOverdue && " (overdue)"}
                              </span>
                            ) : "-"}
                          </TableCell>
                          <TableCell>
                            <Badge color={statusColor[p.status] ?? "light"}>{p.status}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {/* No per-bill Pay: a payment is recorded once
                                  (Record Payment, or a tagged statement row)
                                  and settles bills oldest-first. */}
                              {isOwner && (
                                <button
                                  onClick={() => openEditModal(p)}
                                  className="text-xs font-medium text-brand-500 hover:text-brand-600 dark:text-brand-400"
                                >
                                  Edit
                                </button>
                              )}
                              {/* A bill a purchase raised is deleted with the
                                  purchase; deleting it alone left the purchase
                                  owing nothing. Only hand-typed bills go here.
                                  Paid ones too: money that reached the bill by
                                  oldest-first settling goes back to its payment,
                                  and the server says so when it cannot. */}
                              {isOwner && !p.source_type && (
                                <button
                                  onClick={() => setConfirmBillDelete(p.id)}
                                  className="text-xs font-medium text-error-500 hover:text-error-600 dark:text-error-400"
                                >
                                  Delete
                                </button>
                              )}
                              <button
                                onClick={() => openPaymentHistory(p)}
                                className="text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400"
                              >
                                History
                              </button>
                              {isOwner && <AttachmentViewer sourceType="payable" sourceId={p.id} count={p.attachment_count ?? 0} />}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
            <Pagination total={total} limit={limit} offset={offset} onPrev={handlePrev} onNext={handleNext} />
          </div>
        </>
      )}

      {/* ---- BALANCES TAB ---- */}
      {tab === "balances" && (
        <div className="overflow-hidden card">
          {balancesLoading ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">Loading...</div>
          ) : balances.length === 0 && !error ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">No supplier balances</div>
          ) : balances.length === 0 ? null : (
            <div className="max-w-full overflow-x-auto">
              <Table padded>
                <TableHeader>
                  <TableRow>
                    <TableCell isHeader>Supplier</TableCell>
                    <TableCell isHeader>Total Billed</TableCell>
                    <TableCell isHeader>Total Paid</TableCell>
                    <TableCell isHeader>Outstanding</TableCell>
                    <TableCell isHeader>Credit</TableCell>
                    <TableCell isHeader>Advance paid</TableCell>
                    <TableCell isHeader>Overdue Bills</TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {balances.map((b) => (
                    <TableRow key={b.supplier_id}>
                      <TableCell className="font-medium">
                        <button onClick={() => navigate(`/payables/supplier/${b.supplier_id}`)} className="text-brand-500 hover:text-brand-600 hover:underline dark:text-brand-400">
                          {b.supplier_name}
                        </button>
                      </TableCell>
                      <TableCell>{"\u20B9"}{b.total_billed.toLocaleString("en-IN")}</TableCell>
                      <TableCell>{"\u20B9"}{b.total_paid.toLocaleString("en-IN")}</TableCell>
                      <TableCell className={b.outstanding > 0 ? "font-medium text-error-600 dark:text-error-400" : ""}>
                        {"\u20B9"}{b.outstanding.toLocaleString("en-IN")}
                      </TableCell>
                      <TableCell className={b.credit > 0 ? "font-medium text-brand-600 dark:text-brand-400" : "text-gray-400"}>
                        {b.credit > 0 ? `\u20B9${b.credit.toLocaleString("en-IN")}` : "-"}
                      </TableCell>
                      <TableCell className={b.advance_held > 0 ? "font-medium text-brand-600 dark:text-brand-400" : "text-gray-400"}>
                        {b.advance_held > 0 ? `\u20B9${b.advance_held.toLocaleString("en-IN")}` : "-"}
                      </TableCell>
                      <TableCell>
                        {b.overdue_count > 0 ? (
                          <Badge color="error">{b.overdue_count} overdue</Badge>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell className="font-bold">Total</TableCell>
                    <TableCell className="font-bold">{"\u20B9"}{balances.reduce((s, b) => s + b.total_billed, 0).toLocaleString("en-IN")}</TableCell>
                    <TableCell className="font-bold">{"\u20B9"}{balances.reduce((s, b) => s + b.total_paid, 0).toLocaleString("en-IN")}</TableCell>
                    <TableCell className="font-bold text-error-600 dark:text-error-400">{"\u20B9"}{balances.reduce((s, b) => s + b.outstanding, 0).toLocaleString("en-IN")}</TableCell>
                    <TableCell className="font-bold text-brand-600 dark:text-brand-400">{"\u20B9"}{balances.reduce((s, b) => s + b.credit, 0).toLocaleString("en-IN")}</TableCell>
                    <TableCell className="font-bold text-brand-600 dark:text-brand-400">{"\u20B9"}{balances.reduce((s, b) => s + (b.advance_held ?? 0), 0).toLocaleString("en-IN")}</TableCell>
                    <TableCell>{""}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}

      {/* ---- CREATE BILL MODAL ---- */}
      <Modal isOpen={showCreate} onClose={() => { setShowCreate(false); resetBillForm(); }} className="max-w-md">
        <div className="p-6">
          <h3 className="mb-4 text-lg font-semibold text-gray-800 dark:text-white/90">{editingId ? "Edit Bill" : "New Bill"}</h3>
          {error && showCreate && (
            <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
              {error}
            </div>
          )}
          <div className="space-y-4">
            <div>
              <Label>Supplier *</Label>
              <select value={formSupplierId} onChange={(e) => setFormSupplierId(e.target.value)} className={selectClass}>
                <option value="">Select supplier</option>
                {suppliers.filter((s) => s.status === "active").map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Bill Date *</Label>
                <Input type="date" value={formDate} onChange={(e) => setFormDate(e.target.value)} />
              </div>
              <div>
                <Label>Due Date</Label>
                <Input type="date" value={formDueDate} onChange={(e) => setFormDueDate(e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Bill Number</Label>
                <Input type="text" value={formBillNumber} onChange={(e) => setFormBillNumber(e.target.value)} placeholder="INV-001" />
              </div>
              <div>
                <Label>Amount *</Label>
                <Input type="number" value={formAmount} onChange={(e) => setFormAmount(e.target.value)} min="0.01" step="0.01" placeholder="0.00" />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Input type="text" value={formNotes} onChange={(e) => setFormNotes(e.target.value)} />
            </div>
            <AttachmentUpload
              attachments={formAttachments}
              onUpload={(a) => {
                setFormAttachments((prev) => [...prev, a]);
                setFormAttachmentIds((prev) => [...prev, a.id]);
              }}
              onDelete={(id) => {
                setFormAttachments((prev) => prev.filter((a) => a.id !== id));
                setFormAttachmentIds((prev) => prev.filter((aid) => aid !== id));
              }}
              disabled={saving}
            />
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <Button variant="outline" onClick={() => { setShowCreate(false); resetBillForm(); }}>Cancel</Button>
            <Button onClick={handleSaveBill} disabled={saving || !formSupplierId || !formDate || !formAmount}>
              {saving ? "Saving..." : editingId ? "Update Bill" : "Create Bill"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ---- EDIT PAYMENT MODAL ---- */}
      <Modal isOpen={showPayment} onClose={() => setShowPayment(false)} className="max-w-md">
        <div className="p-6">
          <h3 className="mb-2 text-lg font-semibold text-gray-800 dark:text-white/90">Edit Payment</h3>
          {error && showPayment && (
            <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
              {error}
            </div>
          )}
          {payTarget && (
            <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
              {payTarget.supplier_name} — Bill {payTarget.bill_number || `#${payTarget.id}`}
              {" "}(Outstanding: {"\u20B9"}{(payTarget.total_amount - payTarget.paid_amount).toLocaleString("en-IN")})
            </p>
          )}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Payment Date *</Label>
                <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} />
              </div>
              <div>
                <Label>Amount *</Label>
                <Input type="number" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} min="0.01" step="0.01" data-testid="payment-amount-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Method</Label>
                <select value={payMethod} onChange={(e) => setPayMethod(e.target.value)} className={selectClass}>
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cheque">Cheque</option>
                  <option value="upi">UPI</option>
                </select>
              </div>
              <div>
                <Label>Reference</Label>
                <Input type="text" value={payReference} onChange={(e) => setPayReference(e.target.value)} placeholder="Txn ID / Cheque #" />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Input type="text" value={payNotes} onChange={(e) => setPayNotes(e.target.value)} />
            </div>
            {payTarget && allocationPreview.items.length > 0 && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-900">
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Allocation preview</p>
                <ul className="space-y-1.5 text-sm">
                  {allocationPreview.items.map((item, idx) => {
                    const b = item.payable;
                    const label = b.bill_number || `#${b.id}`;
                    const dateStr = displayDate(b.date);
                    const settled = b.paid_amount + item.amount >= b.total_amount - SETTLED_EPS;
                    const isFirst = idx === 0;
                    return (
                      <li key={b.id} className="flex items-center justify-between">
                        <span className="text-gray-700 dark:text-gray-300">
                          {isFirst ? "→" : "↳"} Bill {label} <span className="text-xs text-gray-400">({dateStr})</span>
                          {settled ? (
                            <span className="ml-2 text-xs text-success-600 dark:text-success-400">paid in full</span>
                          ) : (
                            <span className="ml-2 text-xs text-warning-600 dark:text-warning-400">partial</span>
                          )}
                        </span>
                        <span className="font-medium text-gray-800 dark:text-white/90">
                          {"₹"}{item.amount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                        </span>
                      </li>
                    );
                  })}
                  {allocationPreview.advance > 0 && (
                    <li className="flex items-center justify-between border-t border-gray-200 pt-1.5 dark:border-gray-700">
                      <span className="text-gray-700 dark:text-gray-300">
                        ↳ Supplier credit <span className="text-xs text-gray-400">(auto-applies to next bill)</span>
                      </span>
                      <span className="font-medium text-brand-600 dark:text-brand-400">
                        {"₹"}{allocationPreview.advance.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                      </span>
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowPayment(false)}>Cancel</Button>
            <Button onClick={handleRecordPayment} disabled={saving || !payDate || (parseFloat(payAmount) || 0) <= 0}>
              {saving ? "Saving..." : "Update Payment"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ---- PAYMENT HISTORY MODAL ---- */}
      <Modal isOpen={showHistory} onClose={() => setShowHistory(false)} className="max-w-lg">
        <div className="p-6">
          <h3 className="mb-2 text-lg font-semibold text-gray-800 dark:text-white/90">Payment History</h3>
          {error && showHistory && (
            <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
              {error}
            </div>
          )}
          {historyPayable && (
            <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
              {historyPayable.supplier_name} — Bill {historyPayable.bill_number || `#${historyPayable.id}`}
              {" "}({"\u20B9"}{historyPayable.total_amount.toLocaleString("en-IN")})
            </p>
          )}
          {historyLoading ? (
            <p className="py-4 text-center text-sm text-gray-400">Loading...</p>
          ) : payments.length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-400">No payments recorded yet.</p>
          ) : (
            <div className="max-w-full overflow-x-auto">
              <Table padded>
                <TableHeader>
                  <TableRow>
                    <TableCell isHeader>Date</TableCell>
                    <TableCell isHeader>Amount</TableCell>
                    <TableCell isHeader>Method</TableCell>
                    <TableCell isHeader>Reference</TableCell>
                    {isOwner && <TableCell isHeader>{""}</TableCell>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((pm) => {
                    // Auto-allocated children can't be edited or deleted in
                    // isolation — operators must act on the parent payment.
                    const isPrimary = !pm.parent_payment_id;
                    return (
                      <TableRow key={pm.id}>
                        <TableCell>{displayDate(pm.date)}</TableCell>
                        <TableCell>{"\u20B9"}{pm.amount.toLocaleString("en-IN")}</TableCell>
                        <TableCell>{methodLabels[pm.method] ?? pm.method}</TableCell>
                        <TableCell>{pm.reference || "-"}</TableCell>
                        {isOwner && (
                          <TableCell className="text-right">
                            {isPrimary && historyPayable ? (
                              <div className="flex justify-end gap-2 text-xs">
                                <button
                                  onClick={() => {
                                    setShowHistory(false);
                                    openPayModal(historyPayable, pm);
                                  }}
                                  className="text-brand-500 hover:text-brand-600 dark:text-brand-400"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => setConfirmPaymentDelete(pm.id)}
                                  disabled={saving}
                                  className="text-error-500 hover:text-error-600 disabled:opacity-50 disabled:cursor-not-allowed dark:text-error-400"
                                >
                                  Delete
                                </button>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">auto</span>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
          <div className="mt-4 flex justify-end">
            <Button variant="outline" onClick={() => setShowHistory(false)}>Close</Button>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        isOpen={confirmBillDelete !== null}
        title="Delete Bill"
        message="Are you sure you want to delete this bill? This action cannot be undone."
        onConfirm={handleDeleteBill}
        onClose={() => setConfirmBillDelete(null)}
      />

      <ConfirmModal
        isOpen={confirmPaymentDelete !== null}
        title="Delete Payment"
        message="Delete this payment? Any auto-allocated children will also be removed and the bill statuses rolled back. This action cannot be undone."
        busy={saving}
        busyLabel="Deleting…"
        onConfirm={handleDeletePayment}
        onClose={() => setConfirmPaymentDelete(null)}
      />
      <RecordCashModal
        isOpen={showRecordPayment}
        onClose={() => setShowRecordPayment(false)}
        kind="payment"
        parties={suppliers.filter((x) => x.status === "active").map((x) => ({ id: x.id, name: x.name }))}
        onSubmit={async ({ party_id, ...rest }) =>
          (await payableService.recordSupplierPayment({ supplier_id: party_id, ...rest })).settled
        }
        onSaved={() => {
          fetchBills();
          fetchBalances();
        }}
      />
    </>
  );
}
